import React, { useState, useEffect, useCallback } from 'react';
import {
  Inbox, Repeat, BarChart3, Store, Package, Home, Wrench,
  AlertTriangle, Shield, Recycle, Truck,
  Calendar, ChevronRight, Loader2, Clock, Info, X, Flag,
} from 'lucide-react';
import { getDashboard, getDistintos } from '../api';
import { COMPANIES } from '../App';

const HIDDEN_TABS = {
  'fix-csp': ['ii', 'ih', 'eco_troca', 'one_stop'],
  'fix-msc': ['recepcao', 'gd', 'one_stop', 'sr', 'ad', 'balcao'],
  'vip': ['one_stop'],
};

const fmtDate = (d) => d.toISOString().split('T')[0];
const fmtDateBR = (s) => { if (!s) return ''; const [y,m,d] = s.split('-'); return `${d}/${m}/${y}`; };
const fmtNum = (n) => new Intl.NumberFormat('pt-BR').format(n || 0);

function parseTimeToSec(s) {
  if (!s) return 0;
  const p = String(s).split(':');
  if (p.length === 3) return +p[0]*3600 + +p[1]*60 + +p[2];
  return 0;
}
function fmtMS(sec) {
  if (!sec || sec <= 0) return '—';
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function sumDaily(arr, ...fields) {
  if (!arr) return 0;
  return arr.reduce((a, d) => a + fields.reduce((s,f) => s + (d[f]||0), 0), 0);
}

function summarizeSection(s) {
  if (!s) return null;
  const ent = s.entradas_diario || [], rep = s.reparos_diario || [];
  return {
    eIw: sumDaily(ent,'iw'), eOw: sumDaily(ent,'ow'),
    eTotal: sumDaily(ent,'iw') + sumDaily(ent,'ow'),
    rIw: sumDaily(rep,'iw'), rOw: sumDaily(rep,'ow'),
    rTotal: sumDaily(rep,'iw') + sumDaily(rep,'ow'),
    backlog: s.backlog || { iw: 0, ow: 0, total: 0 },
  };
}
function summarizeProjeto(s) {
  if (!s) return null;
  return {
    entradas: sumDaily(s.entradas_diario||[], 'total'),
    saidas: sumDaily(s.saidas_diario||[], 'total'),
    backlog: s.backlog,
  };
}
function summarizeRecepcao(arr) {
  if (!arr || arr.length === 0) return null;
  let qtd = 0, tmeW = 0, tmaW = 0;
  arr.forEach(d => {
    const q = d.qtd_tickets || 0; qtd += q;
    tmeW += parseTimeToSec(d.tme) * q;
    tmaW += parseTimeToSec(d.tma) * q;
  });
  return { qtd, tme: qtd>0 ? tmeW/qtd : 0, tma: qtd>0 ? tmaW/qtd : 0 };
}
function summarizeGDTA(g) {
  if (!g) return null;
  return { gds: sumDaily(g.gd_diario||[], 'total'), tas: sumDaily(g.ta_diario||[], 'total') };
}

const METRICAS = [
  { key:'recepcao', titulo:'Recepção', subtitulo:'Senhas Qless · TME · TMA', Icone: Inbox, accent:'#a78bfa', type:'recepcao', fonte:'QLESS',
    descricao:'Quantas senhas foram tiradas no Qless, com o tempo médio de espera (TME) e de atendimento (TMA). Mede o fluxo e a velocidade do balcão de chegada.',
    regraTexto:'Conta TODAS as senhas Qless no período (não há filtro de tipo). TME = média do tempo de espera. TMA = média do tempo de atendimento, ponderadas por quantidade de senhas.',
    filtros:[] },
  { key:'gd', titulo:'GD — TA', subtitulo:'Galaxy Diagnostic · Triagens', Icone: Repeat, accent:'#fbbf24', type:'gdta', fonte:'GSPN',
    descricao:'GD = Galaxy Diagnostic (diagnóstico do aparelho Samsung). TA = Triagens realizadas. Acompanha quantos diagnósticos foram triados no período.',
    regraTexto:'Conta GDs (Galaxy Diagnostics) e TAs (Triagens) da tabela "guias_devolucao", sem filtro de status.',
    filtros:[{ campo:'gd_tipo', label:'Tipo de GD (informativo, sem filtro)', valoresUsados:[] }] },
  { key:'total', titulo:'Total CI + IH', subtitulo:'Volume consolidado de reparos', Icone: BarChart3, accent:'#60a5fa', type:'section', dataKey:'total_ci_ih', fonte:'GSPN',
    descricao:'Volume consolidado de reparos do balcão (CI) somado ao atendimento em domicílio (IH). Visão geral da operação.',
    regraTexto:'Soma Balcão Total (CI) + In Home Total (IH). Vide regras individuais dessas métricas.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['CI','PS','AD','SR','SH','IH','II'] },
      { campo:'type_calc',    label:'Type (categoria)',  valoresUsados:['HHP','NPC','WEA','MON','VD','AV','WM','REF','AC'] },
    ]},
  { key:'balcao', titulo:'Balcão (CI)', subtitulo:'Entradas e reparos no balcão', Icone: Store, accent:'#4fc3f7', type:'section', dataKey:'balcao_total', fonte:'GSPN',
    descricao:'Aparelhos que chegaram, foram reparados e ainda estão na fila do balcão. IW = em garantia, OW = fora de garantia.',
    regraTexto:'Soma das pivots MX + NPC + CE: Tipo de Serviço ∈ {CI, PS, AD, SR (⚠️ bug — deveria sair), SH (só CE)}, Type ∈ {HHP, NPC, WEA, MON, VD, AV}, Garantia Estendida ≠ Extendida.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['CI','PS','AD','SR','SH'] },
      { campo:'type_calc',    label:'Type (categoria)',  valoresUsados:['HHP','NPC','WEA','MON','VD','AV'] },
      { campo:'garantia_ext', label:'Garantia Estendida (exclui Extendida)', valoresUsados:[''] },
    ]},
  { key:'sr', titulo:'Stock Repair', subtitulo:'Reparo em estoque', Icone: Package, accent:'#c084fc', type:'section', dataKey:'sr_total', fonte:'GSPN',
    descricao:'Reparos feitos em peças/aparelhos do estoque, separados do balcão regular.',
    regraTexto:'Tipo de Serviço = SR (Stock Repair).',
    filtros:[{ campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['SR'] }]},
  { key:'ih', titulo:'In Home', subtitulo:'Atendimento em domicílio', Icone: Home, accent:'#34d399', type:'section', dataKey:'ih_total', fonte:'GSPN',
    descricao:'Atendimentos feitos na casa do cliente (TVs, refrigeradores, ar-condicionado, etc).',
    regraTexto:'DA (WM/REF/AC): Tipo de Serviço = IH. CE (MON/VD/AV): Tipo de Serviço ∈ {IH, II}.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['IH','II'] },
      { campo:'type_calc',    label:'Type (categoria)',  valoresUsados:['WM','REF','AC','MON','VD','AV'] },
    ]},
  { key:'ii', titulo:'Instalação Inicial', subtitulo:'Setup inicial', Icone: Wrench, accent:'#fb923c', type:'section', dataKey:'instalacao_inicial', fonte:'GSPN',
    descricao:'Setup inicial de aparelhos novos (II), normalmente TVs e linha branca grandes.',
    regraTexto:'Tipo de Serviço = II AND Eco Troca ≠ "Eco troca".',
    filtros:[{ campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['II'] }]},
  { key:'ad', titulo:'Acidental Damage', subtitulo:'Danos acidentais', Icone: AlertTriangle, accent:'#f87171', type:'projeto', dataKey:'ad', fonte:'GSPN',
    descricao:'Reparos por dano acidental (queda, líquido, etc). Cobertos por seguros tipo Care+.',
    regraTexto:'Tipo de Serviço = AD.',
    filtros:[{ campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['AD'] }]},
  { key:'garantia_ext', titulo:'Garantia Estendida', subtitulo:'GE / Care+', Icone: Shield, accent:'#22d3ee', type:'projeto', dataKey:'garantia_ext', fonte:'GSPN',
    descricao:'Atendimentos cobertos por garantia estendida (Care+ / GE).',
    regraTexto:'Coluna Garantia Estendida = "Extendida".',
    filtros:[{ campo:'garantia_ext', label:'Coluna Garantia Estendida', valoresUsados:['Extendida'] }]},
  { key:'eco_troca', titulo:'Eco Troca', subtitulo:'Trocas ecológicas', Icone: Recycle, accent:'#84cc16', type:'projeto', dataKey:'eco_troca', fonte:'GSPN',
    descricao:'Casos onde o reparo não compensa e o cliente troca por aparelho novo (programa Samsung).',
    regraTexto:'Coluna Eco Troca = "Eco troca".',
    filtros:[{ campo:'eco_troca', label:'Coluna Eco Troca', valoresUsados:['Eco troca'] }]},
  { key:'one_stop', titulo:'One Stop', subtitulo:'Frete / Logística', Icone: Truck, accent:'#f59e0b', type:'projeto', dataKey:'one_stop_dados', fonte:'ONE STOP',
    descricao:'Logística de frete (coleta/entrega de aparelhos via transportadora).',
    regraTexto:'Coluna One Stop = "One Stop".',
    filtros:[{ campo:'one_stop', label:'Coluna One Stop', valoresUsados:['One Stop'] }]},
];

// Cor por sistema-fonte
const FONTE_COLORS = {
  'QLESS':    { bg: 'rgba(167,139,250,0.10)', color: '#6d28d9', border: 'rgba(167,139,250,0.30)' },
  'GSPN':     { bg: 'rgba(96,165,250,0.10)',  color: '#1d4ed8', border: 'rgba(96,165,250,0.30)' },
  'ONE STOP': { bg: 'rgba(245,158,11,0.10)',  color: '#b45309', border: 'rgba(245,158,11,0.30)' },
  'CARE':     { bg: 'rgba(244,114,182,0.10)', color: '#be185d', border: 'rgba(244,114,182,0.30)' },
  'GESTOR':   { bg: 'rgba(52,211,153,0.10)',  color: '#047857', border: 'rgba(52,211,153,0.30)' },
};


const COMPANY_THEME = {
  'jmv':     { color: '#60a5fa' },
  'fix-csp': { color: '#a78bfa' },
  'fix-msc': { color: '#f472b6' },
  'vip':     { color: '#34d399' },
};

// =================== ATOMS ===================

const monoFont = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;

function StoreChip({ company }) {
  const c = COMPANY_THEME[company.slug]?.color || '#475569';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '4px 10px 4px 8px',
      background: 'rgba(15,23,42,0.04)',
      border: '1px solid rgba(15,23,42,0.08)',
      borderRadius: 999,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}aa` }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', letterSpacing: '0.02em' }}>{company.name}</span>
    </div>
  );
}

function DeltaBadge({ value, avg }) {
  if (!isFinite(value) || !avg || avg === 0) return null;
  const delta = ((value - avg) / avg) * 100;
  if (!isFinite(delta)) return null;
  const positivo = delta > 0;
  const neutro = Math.abs(delta) < 0.5;
  const cor = neutro ? '#475569' : positivo ? '#047857' : '#b91c1c';
  const bg = neutro ? 'rgba(156,163,175,0.10)' : positivo ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)';
  const border = neutro ? 'rgba(156,163,175,0.25)' : positivo ? 'rgba(16,185,129,0.30)' : 'rgba(239,68,68,0.30)';
  const sign = neutro ? '' : positivo ? '+' : '';
  return (
    <div style={{
      padding: '5px 10px', borderRadius: 10,
      background: bg, border: `1px solid ${border}`,
      textAlign: 'right',
    }}>
      <p style={{
        fontSize: 9, fontWeight: 700, color: '#475569',
        textTransform: 'uppercase', letterSpacing: '0.10em', whiteSpace: 'nowrap',
      }}>vs Média Empresa</p>
      <p style={{
        fontSize: 16, fontWeight: 800, color: cor, lineHeight: 1.1,
        fontFamily: monoFont, letterSpacing: '-0.02em', marginTop: 2,
      }}>{neutro ? '0%' : `${sign}${delta.toFixed(delta % 1 === 0 ? 0 : 1)}%`}</p>
    </div>
  );
}

// Wrapper que junta StoreChip + DeltaBadge no header do card
function CardHeader({ company, valor, avg }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
      <StoreChip company={company} />
      <DeltaBadge value={valor} avg={avg} />
    </div>
  );
}

function StatPair({ label, value, sub, accent }) {
  return (
    <div>
      <p style={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6,
      }}>{label}</p>
      <p style={{
        fontSize: 28, fontWeight: 700, color: accent || '#0f172a',
        fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1,
      }}>{value}</p>
      {sub && (
        <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 5, fontFamily: monoFont, letterSpacing: '0.02em' }}>{sub}</p>
      )}
    </div>
  );
}

function MicroStat({ label, value, accent }) {
  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(15,23,42,0.025)',
      border: '1px solid rgba(15,23,42,0.04)',
      borderRadius: 8,
    }}>
      <p style={{
        fontSize: 9, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: '0.14em',
      }}>{label}</p>
      <p style={{
        fontSize: 16, fontWeight: 700, color: accent || '#0f172a',
        fontFamily: monoFont, letterSpacing: '-0.02em', marginTop: 4,
      }}>{value}</p>
    </div>
  );
}

function TaxaReparoBadge({ label, pct, color, showFlag }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px', borderRadius: 8,
      background: `${color}12`,
      border: `1px solid ${color}30`,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: '0.06em' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 800, color, fontFamily: monoFont, letterSpacing: '-0.02em' }}>{pct.toFixed(0)}%</span>
      {showFlag && <Flag size={11} color={color} fill={color} />}
    </div>
  );
}

function TaxaReparoBlock({ summary }) {
  const eIw = summary.eIw || 0, rIw = summary.rIw || 0;
  const eOw = summary.eOw || 0, rOw = summary.rOw || 0;
  if (eIw === 0 && eOw === 0) return null;

  const cor = (pct) => pct < 50 ? '#ef4444' : pct < 80 ? '#facc15' : '#10b981';
  const pctIw = eIw > 0 ? (rIw / eIw) * 100 : null;
  const pctOw = eOw > 0 ? (rOw / eOw) * 100 : null;

  return (
    <div style={{
      marginTop: 12, padding: '10px 14px', borderRadius: 10,
      background: 'rgba(15,23,42,0.025)',
      border: '1px solid rgba(15,23,42,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{
          fontSize: 9.5, fontWeight: 700, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.14em',
        }}>Taxa de Reparo</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {pctIw !== null && (
            <TaxaReparoBadge label="IW" pct={pctIw} color={cor(pctIw)} showFlag={pctIw < 50} />
          )}
          {pctOw !== null && (
            <TaxaReparoBadge label="OW" pct={pctOw} color={cor(pctOw)} showFlag={pctOw < 50} />
          )}
        </div>
      </div>
    </div>
  );
}

function BacklogStrip({ backlog }) {
  if (!backlog || backlog.total === undefined) return null;
  return (
    <div style={{
      marginTop: 14, padding: '11px 14px',
      background: 'linear-gradient(90deg, rgba(167,139,250,0.10) 0%, rgba(167,139,250,0.03) 100%)',
      border: '1px solid rgba(167,139,250,0.18)',
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{
          fontSize: 9.5, fontWeight: 700, color: '#6d28d9',
          textTransform: 'uppercase', letterSpacing: '0.14em',
        }}>Backlog atual</span>
        <span style={{
          fontSize: 22, fontWeight: 700, color: '#7e22ce',
          fontFamily: monoFont, letterSpacing: '-0.03em',
        }}>{fmtNum(backlog.total)}</span>
      </div>
      {(backlog.iw !== undefined || backlog.ow !== undefined) && (
        <p style={{
          fontSize: 10.5, color: '#64748b', marginTop: 4,
          fontFamily: monoFont, letterSpacing: '0.02em',
        }}>
          IW <span style={{ color: '#b45309', fontWeight: 700 }}>{backlog.iw || 0}</span>
          {'  '}·  OW <span style={{ color: '#dc2626', fontWeight: 700 }}>{backlog.ow || 0}</span>
        </p>
      )}
    </div>
  );
}

// =================== CARDS ===================

function VerRegraBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 14, width: '100%',
        padding: '8px 12px',
        background: 'rgba(15,23,42,0.025)',
        border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: 8, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        fontSize: 11, fontWeight: 600, color: '#475569', letterSpacing: '0.02em',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(15,23,42,0.06)'; e.currentTarget.style.color = '#1e293b'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15,23,42,0.025)'; e.currentTarget.style.color = '#475569'; }}
    >
      <Info size={11} />
      <span>Ver regra da métrica</span>
    </button>
  );
}

function EmptyCard({ company, onShowRule }) {
  return (
    <div className="warrom-card">
      <StoreChip company={company} />
      <div style={{ padding: '32px 0 8px', textAlign: 'center' }}>
        <p style={{ fontSize: 11, color: '#94a3b8' }}>Sem dados no período</p>
      </div>
      {onShowRule && <VerRegraBtn onClick={onShowRule} />}
    </div>
  );
}

function CardSection({ company, summary, onShowRule }) {
  if (!summary || (summary.eTotal === 0 && summary.rTotal === 0 && (summary.backlog?.total || 0) === 0))
    return <EmptyCard company={company} onShowRule={onShowRule} />;
  return (
    <div className="warrom-card">
      <StoreChip company={company} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <StatPair label="Entradas" value={fmtNum(summary.eTotal)} sub={`IW ${summary.eIw} · OW ${summary.eOw}`} accent="#0369a1" />
        <StatPair label="Reparos" value={fmtNum(summary.rTotal)} sub={`IW ${summary.rIw} · OW ${summary.rOw}`} accent="#15803d" />
      </div>
      <TaxaReparoBlock summary={summary} />
      <BacklogStrip backlog={summary.backlog} />
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

function CardProjeto({ company, summary, onShowRule }) {
  if (!summary || (summary.entradas === 0 && summary.saidas === 0)) return <EmptyCard company={company} onShowRule={onShowRule} />;
  const saldo = summary.entradas - summary.saidas;
  const pct = summary.entradas > 0 ? (summary.saidas / summary.entradas) * 100 : 0;
  const corBarra = pct >= 80 ? '#10b981' : pct >= 50 ? '#facc15' : '#ef4444';
  const saldoCor = saldo > 0 ? '#dc2626' : saldo < 0 ? '#b45309' : '#15803d';
  return (
    <div className="warrom-card">
      <StoreChip company={company} />
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Saídas / Entradas</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: corBarra, fontFamily: monoFont }}>{pct.toFixed(0)}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: '#15803d', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>{fmtNum(summary.saidas)}</span>
          <span style={{ fontSize: 18, color: '#cbd5e1', fontFamily: monoFont, lineHeight: 1 }}>/</span>
          <span style={{ fontSize: 22, fontWeight: 700, color: '#c2410c', fontFamily: monoFont, letterSpacing: '-0.03em', lineHeight: 1 }}>{fmtNum(summary.entradas)}</span>
        </div>
        <div style={{ height: 6, background: 'rgba(15,23,42,0.06)', borderRadius: 99, marginTop: 12, overflow: 'hidden', border: '1px solid rgba(15,23,42,0.04)' }}>
          <div style={{
            height: '100%', width: `${Math.min(pct, 100)}%`,
            background: `linear-gradient(90deg, ${corBarra} 0%, ${corBarra}99 100%)`,
            borderRadius: 99, transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: `0 0 8px ${corBarra}55`,
          }} />
        </div>
      </div>
      {saldo !== 0 && (
        <div style={{
          marginTop: 14, padding: '9px 14px', borderRadius: 10,
          background: `linear-gradient(90deg, ${saldoCor}15 0%, ${saldoCor}05 100%)`,
          border: `1px solid ${saldoCor}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: saldoCor, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {saldo > 0 ? 'Em aberto' : 'Saídas em excesso'}
          </span>
          <span style={{ fontSize: 18, fontWeight: 800, color: saldoCor, fontFamily: monoFont, letterSpacing: '-0.02em' }}>{fmtNum(Math.abs(saldo))}</span>
        </div>
      )}
      {summary.backlog && summary.backlog.total !== undefined && summary.backlog.total !== 0 && (
        <BacklogStrip backlog={summary.backlog} />
      )}
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

function CardRecepcao({ company, summary, onShowRule, avgEmpresa }) {
  if (!summary || summary.qtd === 0) return <EmptyCard company={company} onShowRule={onShowRule} />;
  return (
    <div className="warrom-card">
      <CardHeader company={company} valor={summary.qtd} avg={avgEmpresa} />
      <div style={{ marginTop: 18 }}>
        <p style={{
          fontSize: 9.5, fontWeight: 700, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6,
        }}>Senhas no período</p>
        <p style={{
          fontSize: 38, fontWeight: 800,
          background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          fontFamily: monoFont, letterSpacing: '-0.045em', lineHeight: 1,
        }}>{fmtNum(summary.qtd)}</p>
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <MicroStat label="TME médio" value={fmtMS(summary.tme)} accent="#c2410c" />
        <MicroStat label="TMA médio" value={fmtMS(summary.tma)} accent="#0e7490" />
      </div>
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

function CardGDTA({ company, summary, onShowRule }) {
  if (!summary || (summary.gds === 0 && summary.tas === 0)) return <EmptyCard company={company} onShowRule={onShowRule} />;
  const pct = summary.gds > 0 ? (summary.tas / summary.gds) * 100 : 0;
  const corBarra = pct >= 80 ? '#10b981' : pct >= 50 ? '#facc15' : '#ef4444';
  return (
    <div className="warrom-card">
      <StoreChip company={company} />
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Triagem completada</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: corBarra, fontFamily: monoFont }}>{pct.toFixed(1)}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: '#15803d', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>{fmtNum(summary.tas)}</span>
          <span style={{ fontSize: 18, color: '#cbd5e1', fontFamily: monoFont, lineHeight: 1 }}>/</span>
          <span style={{ fontSize: 22, fontWeight: 700, color: '#b45309', fontFamily: monoFont, letterSpacing: '-0.03em', lineHeight: 1 }}>{fmtNum(summary.gds)}</span>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginLeft: 6, alignSelf: 'baseline' }}>TA / GD</span>
        </div>
        <div style={{ height: 6, background: 'rgba(15,23,42,0.06)', borderRadius: 99, marginTop: 12, overflow: 'hidden', border: '1px solid rgba(15,23,42,0.04)' }}>
          <div style={{
            height: '100%', width: `${Math.min(pct, 100)}%`,
            background: `linear-gradient(90deg, ${corBarra} 0%, ${corBarra}99 100%)`,
            borderRadius: 99, transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: `0 0 8px ${corBarra}55`,
          }} />
        </div>
      </div>
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

// Helper: computa o "movimento" (volume agregado) de cada summary por tipo
function getMovimento(summary, type) {
  if (!summary) return 0;
  if (type === 'recepcao') return summary.qtd || 0;
  if (type === 'gdta') return (summary.gds || 0) + (summary.tas || 0);
  if (type === 'section') return (summary.eTotal || 0) + (summary.rTotal || 0);
  return (summary.entradas || 0) + (summary.saidas || 0); // projeto
}

// =================== MODAL DE REGRA DA MÉTRICA ===================

function RegraModal({ metrica, company, distintos, loading, onClose }) {
  const Icone = metrica.Icone;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 16, border: '1px solid #e2e8f0',
          padding: 0, maxWidth: 560, width: '100%',
          maxHeight: '85vh', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(15,23,42,0.10)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14,
          borderBottom: '1px solid #e2e8f0',
          background: `linear-gradient(135deg, ${metrica.accent}10 0%, transparent 100%)`,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11,
            background: `linear-gradient(135deg, ${metrica.accent}25 0%, ${metrica.accent}08 100%)`,
            border: `1px solid ${metrica.accent}40`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icone size={20} color={metrica.accent} strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.015em' }}>
              {metrica.titulo}
            </h2>
            <p style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>
              Regra aplicada · loja {company.name}
            </p>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)',
            borderRadius: 8, padding: 6, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#475569',
          }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '18px 22px 22px' }}>
          {/* Regra textual */}
          <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: `linear-gradient(135deg, ${metrica.accent}10 0%, ${metrica.accent}03 100%)`,
            border: `1px solid ${metrica.accent}30`,
            marginBottom: 16,
          }}>
            <p style={{
              fontSize: 10, fontWeight: 800, color: metrica.accent,
              textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6,
            }}>Regra de cálculo</p>
            <p style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.55 }}>{metrica.regraTexto}</p>
          </div>

          {/* Filtros por campo */}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 24, color: '#475569' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 12 }}>Carregando valores da base…</span>
            </div>
          )}

          {!loading && metrica.filtros.map((f, idx) => {
            const todos = distintos?.[f.campo] || [];
            const usadosSet = new Set(f.valoresUsados.map(v => String(v)));
            const usados = todos.filter(v => usadosSet.has(String(v)));
            const naoUsados = todos.filter(v => !usadosSet.has(String(v)));

            // Inclui valores em filtro que NÃO existem na base (raros, mas pode acontecer)
            const usadosFantasma = f.valoresUsados.filter(v => v && !todos.map(String).includes(String(v)));

            return (
              <div key={idx} style={{ marginBottom: 18 }}>
                <p style={{
                  fontSize: 10, fontWeight: 800, color: '#475569',
                  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8,
                }}>{f.label}</p>

                {/* Usados (✓) */}
                {(usados.length > 0 || usadosFantasma.length > 0) && (
                  <div style={{ marginBottom: 8 }}>
                    <p style={{ fontSize: 11, color: '#15803d', fontWeight: 700, marginBottom: 6 }}>✓ Incluídos no filtro</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {usados.map(v => (
                        <span key={v} style={tagStyle('#10b98122', '#15803d', '#10b98140')}>{v}</span>
                      ))}
                      {usadosFantasma.map(v => (
                        <span key={`f-${v}`} style={{ ...tagStyle('#facc1522', '#b45309', '#facc1540'), fontStyle: 'italic' }} title="Está no filtro mas não tem registros nesta loja">
                          {v} (sem dados)
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Não usados (✗) */}
                {naoUsados.length > 0 && (
                  <div>
                    <p style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>
                      ✗ Existem na base mas NÃO entram nesta métrica
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {naoUsados.map(v => (
                        <span key={v} style={tagStyle('rgba(15,23,42,0.04)', '#475569', 'rgba(15,23,42,0.08)')}>{v}</span>
                      ))}
                    </div>
                  </div>
                )}

                {todos.length === 0 && f.valoresUsados.length === 0 && (
                  <p style={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic' }}>
                    Sem filtro específico — usa todos os registros.
                  </p>
                )}
              </div>
            );
          })}

          {!loading && metrica.filtros.length === 0 && (
            <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
              Esta métrica não aplica filtro por valor de coluna — usa todos os registros da fonte.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function tagStyle(bg, color, border) {
  return {
    padding: '3px 9px', borderRadius: 999,
    background: bg, color, border: `1px solid ${border}`,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
    fontFamily: monoFont,
  };
}

// =================== FILTER BAR ===================

function FilterBar({ dataInicio, dataFim, setDataInicio, setDataFim, setMes, atalhos, filtroExpandido, setFiltroExpandido, diasNoPeriodo, loading }) {
  const btn = (active) => ({
    padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
    background: active ? 'linear-gradient(180deg, #3b82f6, #2563eb)' : 'rgba(15,23,42,0.04)',
    color: active ? '#fff' : '#475569',
    boxShadow: active ? '0 4px 12px rgba(59,130,246,0.35)' : 'none',
    transition: 'all 0.15s',
  });
  const input = {
    padding: '9px 12px', borderRadius: 10, border: '1px solid #cbd5e1',
    background: '#f8fafc', color: '#0f172a', fontSize: 13, outline: 'none',
  };
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: 'linear-gradient(180deg, #f4f6fb 0%, rgba(255,255,255,0.92) 100%)',
      backdropFilter: 'blur(10px)',
      marginBottom: 24, paddingBottom: 4,
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 14, padding: '14px 18px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Calendar size={14} color="#64748b" />
          <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Período</span>
          <button onClick={() => setMes(0)} style={btn(atalhos.mesAtual)}>Mês Atual</button>
          <button onClick={() => setMes(-1)} style={btn(atalhos.mesPassado)}>Mês Anterior</button>
          <button onClick={() => setMes(-2)} style={btn(!atalhos.mesAtual && !atalhos.mesPassado && !filtroExpandido)}>2 meses</button>
          <button onClick={() => setFiltroExpandido(!filtroExpandido)} style={{ ...btn(filtroExpandido), marginLeft: 'auto' }}>
            {filtroExpandido ? 'Fechar' : 'Filtro livre'}
          </button>
        </div>
        {filtroExpandido && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>De</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={input} />
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Até</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={input} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(15,23,42,0.035)', border: '1px solid rgba(15,23,42,0.06)',
          }}>
            {loading
              ? <Loader2 size={11} color="#fbbf24" style={{ animation: 'spin 1s linear infinite' }} />
              : <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b98188' }} />}
            <span style={{ fontSize: 11, color: '#475569', fontWeight: 600, fontFamily: monoFont }}>
              {fmtDateBR(dataInicio)} → {fmtDateBR(dataFim)} · {diasNoPeriodo}d
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== MAIN ===================

function WarromComparativo() {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoAberto, setInfoAberto] = useState(null);

  // Modal "Ver regra da métrica" + cache de distintos por loja
  const [regraOpen, setRegraOpen] = useState(null); // { metrica, company } | null
  const [distintosCache, setDistintosCache] = useState({});
  const [distintosLoading, setDistintosLoading] = useState(false);

  const abrirRegra = async (metrica, company) => {
    setRegraOpen({ metrica, company });
    if (!distintosCache[company.slug]) {
      setDistintosLoading(true);
      try {
        const res = await getDistintos(company.ascCode);
        setDistintosCache(prev => ({ ...prev, [company.slug]: res.data }));
      } catch (e) {
        setDistintosCache(prev => ({ ...prev, [company.slug]: {} }));
      }
      setDistintosLoading(false);
    }
  };

  const hoje = new Date();
  const ontem = new Date(hoje); ontem.setDate(ontem.getDate() - 1);
  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  const [dataInicio, setDataInicio] = useState(fmtDate(inicioMes));
  const [dataFim, setDataFim] = useState(fmtDate(ontem));
  const [filtroExpandido, setFiltroExpandido] = useState(false);

  const companies = COMPANIES.filter(c => !c.isSalesDay && !c.isWarromComp);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const pairs = await Promise.all(companies.map(async c => {
        try { const res = await getDashboard(c.ascCode, dataInicio, dataFim); return [c.slug, res.data]; }
        catch { return [c.slug, null]; }
      }));
      const map = {}; pairs.forEach(([s, d]) => { map[s] = d; });
      setResults(map);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [dataInicio, dataFim]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  const setMes = (offset) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + offset, 1);
    const fim = offset === 0 ? ontem : new Date(d.getFullYear(), d.getMonth() + 1, 0);
    setDataInicio(fmtDate(d)); setDataFim(fmtDate(fim));
  };

  const dtIni = new Date(dataInicio + 'T00:00:00');
  const dtFim = new Date(dataFim + 'T00:00:00');
  const diasNoPeriodo = Math.round((dtFim - dtIni) / 86400000) + 1;

  const mesAtualAtivo = dataInicio === fmtDate(inicioMes) && dataFim === fmtDate(ontem);
  const mpIni = fmtDate(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
  const mpFim = fmtDate(new Date(hoje.getFullYear(), hoje.getMonth(), 0));
  const mesPassadoAtivo = dataInicio === mpIni && dataFim === mpFim;

  return (
    <div className="warrom-page" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px 48px', fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', -apple-system, sans-serif" }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        .warrom-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 400px));
          gap: 14px;
          justify-content: start;
        }
        .warrom-sec-header { display:flex; align-items:center; gap:14px; margin-bottom:14px; padding:0 4px; }
        .warrom-sec-icon-box { display:inline-flex; align-items:center; justify-content:center; width:38px; height:38px; border-radius:10px; flex-shrink:0; }
        .warrom-sec-title { font-size:16px; font-weight:800; color:#0f172a; letter-spacing:-0.015em; line-height:1.2; }
        .warrom-sec-sub { font-size:11.5px; color:#64748b; margin-top:2px; }
        .warrom-pill { display:inline-flex; align-items:center; gap:6px; font-size:11px; color:#475569; font-weight:700; padding:4px 10px; background:rgba(15,23,42,0.035); border-radius:999px; border:1px solid rgba(15,23,42,0.08); white-space:nowrap; }
        .warrom-card { background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); border-radius:14px; border:1px solid #e2e8f0; padding:16px 18px 18px; box-shadow: 0 1px 0 rgba(15,23,42,0.02) inset, 0 8px 20px rgba(15,23,42,0.08); transition: transform 0.15s, border-color 0.15s; }
        .warrom-card:hover { border-color: #cbd5e1; transform: translateY(-1px); }
        .warrom-card .big-num { font-size:28px; }
        .warrom-card .recep-num { font-size:38px; }
        .warrom-filter-row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .warrom-filter-btn { padding:8px 16px; border-radius:10px; border:none; cursor:pointer; font-size:12px; font-weight:700; letter-spacing:0.02em; transition:all 0.15s; }
        .warrom-legend { display:flex; align-items:center; gap:16px; flex-wrap:wrap; padding:12px 18px; margin-bottom:24px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); border-radius:12px; border:1px solid #e2e8f0; }
        @media (max-width: 600px) {
          .warrom-page { padding: 14px 10px 32px; }
          .warrom-grid { grid-template-columns: 1fr; gap: 10px; }
          .warrom-card { padding: 14px 16px 16px; border-radius: 12px; }
          .warrom-card .big-num { font-size: 24px; }
          .warrom-card .recep-num { font-size: 32px; }
          .warrom-sec-title { font-size: 15px; }
          .warrom-sec-sub { font-size: 11px; }
          .warrom-sec-icon-box { width:34px; height:34px; }
          .warrom-filter-btn { padding:7px 12px; font-size:11.5px; }
          .warrom-legend { padding:10px 14px; gap:12px; margin-bottom:18px; }
        }
        @media (max-width: 380px) {
          .warrom-sec-header { gap: 10px; }
          .warrom-pill { font-size: 10px; padding: 3px 8px; }
        }
      `}</style>

      <FilterBar
        dataInicio={dataInicio} dataFim={dataFim}
        setDataInicio={setDataInicio} setDataFim={setDataFim}
        setMes={setMes}
        atalhos={{ mesAtual: mesAtualAtivo, mesPassado: mesPassadoAtivo }}
        filtroExpandido={filtroExpandido} setFiltroExpandido={setFiltroExpandido}
        diasNoPeriodo={diasNoPeriodo}
        loading={loading}
      />

      {/* Legenda das lojas */}
      <div className="warrom-legend">
        <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Autorizadas</span>
        {companies.map(c => (
          <div key={c.slug} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: COMPANY_THEME[c.slug]?.color,
              boxShadow: `0 0 8px ${COMPANY_THEME[c.slug]?.color}aa`,
            }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          padding: 48, color: '#475569',
          background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0',
        }}>
          <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Carregando dados das lojas…</span>
        </div>
      )}
      {error && (
        <div style={{
          padding: 20, color: '#dc2626',
          background: 'rgba(239,68,68,0.08)', borderRadius: 12,
          border: '1px solid rgba(239,68,68,0.25)', textAlign: 'center',
        }}>{error}</div>
      )}

      {!loading && METRICAS.map(metrica => {
        const cardHasData = ({ summary }) => {
          if (!summary) return false;
          if (metrica.type === 'recepcao') return summary.qtd > 0;
          if (metrica.type === 'gdta') return summary.gds > 0 || summary.tas > 0;
          if (metrica.type === 'section') return summary.eTotal > 0 || summary.rTotal > 0 || (summary.backlog?.total || 0) > 0;
          return summary.entradas > 0 || summary.saidas > 0;
        };
        const cards = companies
          .filter(c => !(HIDDEN_TABS[c.slug] || []).includes(metrica.key))
          .map(c => {
            const data = results[c.slug];
            if (!data) return { company: c, summary: null };
            let summary;
            if (metrica.type === 'recepcao') summary = summarizeRecepcao(data.recepcao_diario);
            else if (metrica.type === 'gdta') summary = summarizeGDTA(data.gd_ta);
            else if (metrica.type === 'section') summary = summarizeSection(data[metrica.dataKey]);
            else summary = summarizeProjeto(data[metrica.dataKey]);
            return { company: c, summary };
          })
          .filter(cardHasData);
        if (cards.length === 0) return null;

        // Média do "movimento" entre as lojas (ignorando as zeradas)
        const movimentos = cards
          .map(({ summary }) => getMovimento(summary, metrica.type))
          .filter(v => v > 0);
        const avgEmpresa = movimentos.length > 0
          ? movimentos.reduce((a, b) => a + b, 0) / movimentos.length
          : 0;

        const Icone = metrica.Icone;

        const aberto = infoAberto === metrica.key;
        return (
          <section key={metrica.key} style={{ marginBottom: 32 }}>
            <header className="warrom-sec-header">
              <div className="warrom-sec-icon-box" style={{
                background: `linear-gradient(135deg, ${metrica.accent}20 0%, ${metrica.accent}08 100%)`,
                border: `1px solid ${metrica.accent}30`,
              }}>
                <Icone size={18} color={metrica.accent} strokeWidth={2.2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h2 className="warrom-sec-title">{metrica.titulo}</h2>
                  <button
                    onClick={() => setInfoAberto(aberto ? null : metrica.key)}
                    aria-label={aberto ? 'Fechar informação' : 'Ver explicação'}
                    style={{
                      background: aberto ? `${metrica.accent}22` : 'rgba(15,23,42,0.04)',
                      border: `1px solid ${aberto ? metrica.accent + '50' : 'rgba(15,23,42,0.08)'}`,
                      borderRadius: 999, cursor: 'pointer',
                      width: 22, height: 22, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Info size={12} color={aberto ? metrica.accent : '#475569'} strokeWidth={2.5} />
                  </button>
                  {metrica.fonte && (() => {
                    const ft = FONTE_COLORS[metrica.fonte] || FONTE_COLORS['GSPN'];
                    return (
                      <span
                        title={`Dados vindos do sistema ${metrica.fonte}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 9px',
                          background: ft.bg,
                          border: `1px solid ${ft.border}`,
                          borderRadius: 999,
                          fontSize: 10, fontWeight: 800,
                          color: ft.color, letterSpacing: '0.08em',
                          fontFamily: monoFont,
                          textTransform: 'uppercase',
                        }}
                      >
                        Fonte · {metrica.fonte}
                      </span>
                    );
                  })()}
                </div>
                <p className="warrom-sec-sub">{metrica.subtitulo}</p>
              </div>
              <span className="warrom-pill">{cards.length} {cards.length === 1 ? 'loja' : 'lojas'}</span>
            </header>

            {aberto && (
              <div style={{
                marginBottom: 14,
                padding: '14px 16px',
                background: `linear-gradient(135deg, ${metrica.accent}10 0%, ${metrica.accent}03 100%)`,
                border: `1px solid ${metrica.accent}30`,
                borderRadius: 12,
                fontSize: 13, lineHeight: 1.55, color: '#334155',
                position: 'relative',
              }}>
                <button
                  onClick={() => setInfoAberto(null)}
                  aria-label="Fechar"
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    background: 'transparent', border: 'none',
                    cursor: 'pointer', padding: 4,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    color: '#475569',
                  }}
                ><X size={14} /></button>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingRight: 24 }}>
                  <Info size={15} color={metrica.accent} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{metrica.descricao}</span>
                </div>
              </div>
            )}

            <div className="warrom-grid">
              {cards.map(({ company, summary }) => {
                const onShowRule = () => abrirRegra(metrica, company);
                const props = { company, summary, onShowRule, avgEmpresa };
                if (metrica.type === 'recepcao') return <CardRecepcao key={company.slug} {...props} />;
                if (metrica.type === 'gdta') return <CardGDTA key={company.slug} {...props} />;
                if (metrica.type === 'section') return <CardSection key={company.slug} {...props} />;
                return <CardProjeto key={company.slug} {...props} />;
              })}
            </div>
          </section>
        );
      })}

      {regraOpen && (
        <RegraModal
          metrica={regraOpen.metrica}
          company={regraOpen.company}
          distintos={distintosCache[regraOpen.company.slug]}
          loading={distintosLoading}
          onClose={() => setRegraOpen(null)}
        />
      )}
    </div>
  );
}

export default WarromComparativo;
